import type { AxiosInstance } from 'axios';
import { injectable, inject } from 'inversify';
import ThirdPartyTypes from '../../di/ThirdPartyTypes';
import { ConnectApiEndpoints } from '../../constants/apiRoutes';
import { CIS_ACCOUNT_BEARER, CONNECT_BEARER } from '../../constants/constants';
import {
  IConnectApi,
  ICreateTuUserParams,
  ICreateTuUser,
  ILoqateFind,
  ILoqateRetrieve,
  ISendOtp,
  ICreditData,
  IValidateOtp,
  IKbaAnswers,
  IKbaQuestions,
  ICreateStoredUserParams,
  ICreateStoredUser
} from './IConnectApi';
import { DataSourceType } from '../../types/DataSourceType';
import ICreateConsentDto from '../dto/ICreateConsentDto';
import IConsentRequest from '../dto/IConsentRequest';
import { localStorageContainer } from '../services';
import { KbaAnswersSubmit } from '../../containers/kba/interfaces';

@injectable()
export default class ConnectApi implements IConnectApi {
  @inject(ThirdPartyTypes.Axios)
  private apiClient!: AxiosInstance;

  public async createTuUser({ url, ...otherParams }: ICreateTuUserParams): Promise<ICreateTuUser> {
    const response = await this.apiClient.post(
      ConnectApiEndpoints.createTuUser(url),
      { ...otherParams },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorageContainer.getTokenByClientId(CONNECT_BEARER)}`
        }
      }
    );
    if (CIS_ACCOUNT_BEARER in (response.headers || {})) {
      localStorageContainer.setTokenByClientId(CIS_ACCOUNT_BEARER, response.headers[CIS_ACCOUNT_BEARER]);
    }
    return response.data;
  }
  public async createStoredUser({ url, ...otherParams }: ICreateStoredUserParams): Promise<ICreateStoredUser> {
    const response = await this.apiClient.post(
      ConnectApiEndpoints.createStoredUser(url),
      { ...otherParams },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorageContainer.getTokenByClientId(CONNECT_BEARER)}`
        }
      }
    );
    if (CIS_ACCOUNT_BEARER in (response.headers || {})) {
      localStorageContainer.setTokenByClientId(CIS_ACCOUNT_BEARER, response.headers[CIS_ACCOUNT_BEARER]);
    }
    return response.data;
  }

  public async sendOtp(url: string, clientId: string, userId: string, tuUserId: string): Promise<ISendOtp> {
    return (
      await this.apiClient.post(
        ConnectApiEndpoints.sendOtp(url),
        {
          applicationId: clientId,
          userId: userId,
          tuUserId: tuUserId
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorageContainer.getTokenByClientId(CONNECT_BEARER)}`
          }
        }
      )
    ).data;
  }

  public async validateOtp(
    url: string,
    clientId: string,
    userId: string,
    tuUserId: string,
    code: string
  ): Promise<IValidateOtp> {
    const response = await this.apiClient.post(
      ConnectApiEndpoints.validateOtp(url),
      {
        ApplicationId: clientId,
        UserId: userId,
        TuUserId: tuUserId,
        OneTimeCode: code
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorageContainer.getTokenByClientId(CONNECT_BEARER)}`
        }
      }
    );

    if (CIS_ACCOUNT_BEARER in (response.headers || {})) {
      localStorageContainer.setTokenByClientId(CIS_ACCOUNT_BEARER, response.headers[CIS_ACCOUNT_BEARER]);
    }

    return response.data;
  }

  public async loqateFind(url: string, searchTerm?: string, containerId?: string): Promise<ILoqateFind> {
    return (
      await this.apiClient.get(ConnectApiEndpoints.loqateFind(url), {
        params: {
          searchTerm,
          containerId
        },
        headers: {
          Authorization: `Bearer ${localStorageContainer.getTokenByClientId(CONNECT_BEARER)}`
        }
      })
    ).data;
  }

  public async loqateRetrieve(url: string, addressId: string): Promise<ILoqateRetrieve> {
    return (
      await this.apiClient.get(ConnectApiEndpoints.loqateRetrieve(url), {
        params: {
          addressId
        },
        headers: {
          Authorization: `Bearer ${localStorageContainer.getTokenByClientId(CONNECT_BEARER)}`
        }
      })
    ).data;
  }

  public async getCreditDataAccounts(url: string): Promise<ICreditData> {
    return (
      await this.apiClient.get(ConnectApiEndpoints.getCreditDataAccounts(url), {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorageContainer.getTokenByClientId(CIS_ACCOUNT_BEARER)}`
        }
      })
    ).data;
  }

  public async getKbaQuestions(
    url: string,
    userId: string,
    tuUserId: string,
    applicationId: string
  ): Promise<IKbaQuestions> {
    return (
      await this.apiClient.post(
        ConnectApiEndpoints.getKbaQuestions(url),
        {
          applicationId: applicationId,
          userId: userId,
          tuUserId: tuUserId
        },
        {
          headers: {
            Authorization: `Bearer ${localStorageContainer.getTokenByClientId(CONNECT_BEARER)}`
          }
        }
      )
    ).data;
  }

  public async sendKbaAnswers(
    url: string,
    userId: string,
    tuUserId: string,
    clientId: string,
    kbaAnswers: KbaAnswersSubmit[]
  ): Promise<IKbaAnswers> {
    const response = await this.apiClient.post(
      ConnectApiEndpoints.submitKbaAnswers(url),
      {
        applicationId: clientId,
        userId: userId,
        tuUserId: tuUserId,
        answers: kbaAnswers
      },
      {
        headers: {
          Authorization: `Bearer ${localStorageContainer.getTokenByClientId(CONNECT_BEARER)}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (CIS_ACCOUNT_BEARER in (response.headers || {})) {
      localStorageContainer.setTokenByClientId(CIS_ACCOUNT_BEARER, response.headers[CIS_ACCOUNT_BEARER]);
    }

    return response?.data;
  }

  public async createConsent(
    url: string,
    clientId: string,
    customerReference: string,
    validationToken: string,
    providerId: number,
    providerName: string,
    dataSource: string,
    dataSourceType: DataSourceType,
    countryCode: string,
    configurationId?: string | undefined,
    maxConsentDays?: number | undefined,
    invitationId?: string | undefined,
    userId?: string | null
  ): Promise<ICreateConsentDto> {
    const data: IConsentRequest = {
      applicationId: clientId,
      customerReference: customerReference,
      validationToken: validationToken,
      providerId: providerId,
      providerName: providerName,
      dataSource: dataSource,
      dataSourceType: dataSourceType,
      maxConsentDays: maxConsentDays,
      countryCode: countryCode,
      configurationName: configurationId || 'default',
      ...(invitationId && { invitationId }),
      userId: userId
    };

    const response = await this.apiClient.post<ICreateConsentDto>(ConnectApiEndpoints.createConsent(url), data);
    if (CONNECT_BEARER in (response.headers || {})) {
      localStorageContainer.setTokenByClientId(CONNECT_BEARER, response.headers[CONNECT_BEARER]);
    }
    return response.data;
  }
}
